import { Link, useLocation } from "react-router-dom";
import {
  ArrowRight,
  BadgePercent,
  CheckCircle2,
  ExternalLink,
} from "lucide-react";
import LanguageSwitch from "@/components/LanguageSwitch";
import { openaiPartnerContent } from "@/data/openaiPartnerContent";
import { useHydratedSearch } from "@/hooks/useHydratedSearch";
import { getPageLocale, getPageMarket, localizedPath, partnerCallHref } from "@/utils/pageLocale";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { trackClick } from "@/utils/posthog";

const PRESS_RELEASE_URL =
  "https://www.prnewswire.com/news-releases/tech-leaders-expands-ai-adoption-support-as-an-openai-smb-channel-partner-302831915.html";

const OpenAIPartner = () => {
  const { pathname } = useLocation();
  const search = useHydratedSearch();
  const locale = getPageLocale(pathname);
  const copy = openaiPartnerContent[locale];
  const market = getPageMarket(locale, search);
  const callHref = (location: string, campaign = "openai-partner") =>
    partnerCallHref(locale, location, campaign, search);
  return (
    <div className="min-h-screen bg-background">
      <SEO
        title={copy.seoTitle}
        description={copy.seoDescription}
        keywords={copy.keywords}
        languageAlternates={[
          { language: "en", path: "/openai-partner" },
          { language: "pt-BR", path: "/pt-br/openai-partner" },
          { language: "x-default", path: "/openai-partner" },
        ]}
      />
      <Navigation locale={locale}>
        <LanguageSwitch path="/openai-partner" />
      </Navigation>

      <main>
        <section className="relative overflow-hidden bg-gradient-to-br from-background via-background to-orange-500/10 px-4 pb-20 pt-32 sm:px-6 lg:px-8">
          <div className="absolute inset-0 bg-grid-white/[0.02] bg-[size:52px_52px]" />
          <div className="relative z-10 mx-auto max-w-5xl text-center">
            <div className="mb-8 inline-flex items-center gap-3 rounded-full bg-[#f7f7f2] px-4 py-2.5 text-slate-950 shadow-lg shadow-black/20">
              <img src="/openai-wordmark.svg" alt="OpenAI" className="h-4 w-auto" />
              <span className="h-4 w-px bg-black/20" aria-hidden="true" />
              <span className="text-xs font-bold tracking-[0.12em]">SMB CHANNEL PARTNER</span>
            </div>

            <p className="mb-5 text-sm font-semibold uppercase tracking-[0.18em] text-orange-400">

              {copy.announcement}

            </p>
            <h1 className="mx-auto mb-6 max-w-4xl text-4xl font-bold text-foreground sm:text-5xl lg:text-6xl">
              {copy.headline}
            </h1>
            <p className="mx-auto mb-6 max-w-3xl text-lg leading-relaxed text-muted-foreground sm:text-xl">
              {copy.intro}
            </p>
            <a
              href="#partner-pricing"
              className="mb-9 inline-flex items-center gap-2 rounded-full border border-orange-500/40 bg-orange-500/10 px-4 py-2 text-sm font-semibold text-orange-300 transition-colors hover:bg-orange-500/20"
              onClick={() => {
                trackClick("OpenAI Partner Page - Pricing Pill", {
                  language: locale,
                  market,
                  location: "partner_page_hero",
                  destination: "#partner-pricing",
                });
              }}
            >
              <BadgePercent className="h-4 w-4" aria-hidden="true" />
              {copy.pricingPill}
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </a>

            <div className="flex flex-col flex-wrap items-center justify-center gap-3 md:flex-row">
              <Button
                asChild
                size="lg"
                className="h-auto min-h-11 max-w-full whitespace-normal py-3 text-center bg-gradient-to-r from-orange-500 to-red-600 font-semibold text-white hover:from-orange-600 hover:to-red-700"
              >
                <Link
                  to={callHref("partner_page_hero", "openai-partner")}
                  onClick={() => {
                    trackClick("OpenAI Partner Page - Schedule Call", {
                      language: locale,
                      market,
                      location: "partner_page_hero",
                      destination: localizedPath("/ai-call", locale),
                    });
                  }}
                >
                  {copy.heroCta}
                  <ArrowRight aria-hidden="true" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="h-auto min-h-11 max-w-full whitespace-normal py-3 text-center font-semibold">
                <a
                  href={PRESS_RELEASE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => {
                    trackClick("OpenAI Partner Page - Read Press Release", {
                      language: locale,
                      market,
                      location: "partner_page_hero",
                      destination: "pr_newswire",
                    });
                  }}
                >
                  {copy.pressCta}
                  <ExternalLink aria-hidden="true" />
                </a>
              </Button>
            </div>

            <p className="mt-8 text-sm text-muted-foreground">

              {copy.proof}

            </p>
          </div>
        </section>


        <section
          id="partner-pricing"
          className="scroll-mt-24 px-4 py-20 sm:px-6 lg:px-8"
          aria-labelledby="partner-pricing-heading"
        >
          <div className="mx-auto max-w-6xl">
            <div className="mx-auto mb-12 max-w-3xl text-center">
              <p className="mb-3 text-sm font-semibold uppercase tracking-[0.18em] text-orange-400">
                {copy.pricingLabel}
              </p>
              <h2
                id="partner-pricing-heading"
                className="mb-4 text-3xl font-bold text-foreground sm:text-4xl"
              >
                {copy.pricingHeadline}
                </h2>
              <p className="text-lg leading-relaxed text-muted-foreground">
                {copy.pricingIntro}
              </p>
            </div>

            <div className="grid gap-6 md:grid-cols-3">
              {copy.pricingBenefits.map(({ title, description, icon: Icon }) => (
                <article
                  key={title}
                  className="rounded-2xl border border-orange-500/30 bg-gradient-to-b from-orange-500/10 to-card p-7"
                >
                  <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-xl bg-orange-500/15 text-orange-400">
                    <Icon className="h-6 w-6" aria-hidden="true" />
                  </div>
                  <h3 className="mb-3 text-xl font-bold text-foreground">{title}</h3>
                  <p className="leading-relaxed text-muted-foreground">{description}</p>
                </article>
              ))}
            </div>

            <div className="mt-10 grid gap-8 rounded-2xl border border-border bg-card p-7 sm:p-8 lg:grid-cols-[1fr_auto] lg:items-center">
              <div>
                <h3 className="mb-4 text-xl font-bold text-foreground">
                  {copy.pricingHow}
                </h3>
                <ol className="space-y-3">
                  {copy.pricingSteps.map((step, index) => (
                    <li key={step} className="flex items-start gap-3 text-foreground">
                      <span
                        className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-orange-500/15 text-xs font-bold text-orange-400"
                        aria-hidden="true"
                      >
                        {index + 1}
                      </span>
                      <span>{step}</span>
                    </li>
                  ))}
                </ol>
                <p className="mt-5 text-sm text-muted-foreground">
                  {copy.renewal}
                </p>
              </div>
              <Button
                asChild
                size="lg"
                className="h-auto min-h-11 max-w-full whitespace-normal py-3 text-center bg-gradient-to-r from-orange-500 to-red-600 font-semibold text-white hover:from-orange-600 hover:to-red-700"
              >
                <Link
                  to={callHref("partner_page_pricing", "openai-partner-pricing")}
                  onClick={() => {
                    trackClick("OpenAI Partner Page - Pricing Quote CTA", {
                      language: locale,
                      market,
                      location: "partner_page_pricing",
                      destination: localizedPath("/ai-call", locale),
                    });
                  }}
                >
                  {copy.quoteCta}
                  <ArrowRight aria-hidden="true" />
                </Link>
              </Button>
            </div>
          </div>
        </section>

        <section className="border-y border-border bg-secondary/20 px-4 py-20 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-6xl">
            <div className="mx-auto mb-12 max-w-3xl text-center">
              <p className="mb-3 text-sm font-semibold uppercase tracking-[0.18em] text-orange-400">
                {copy.adoptionLabel}
              </p>
              <h2 className="mb-4 text-3xl font-bold text-foreground sm:text-4xl">
                {copy.adoptionHeadline}
              </h2>
              <p className="text-lg leading-relaxed text-muted-foreground">
                {copy.adoptionIntro}
              </p>
            </div>

            <div className="grid gap-6 md:grid-cols-3">
              {copy.adoptionSteps.map(({ title, description, icon: Icon }) => (
                <article key={title} className="rounded-2xl border border-border bg-card p-7">
                  <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-xl bg-orange-500/15 text-orange-400">
                    <Icon className="h-6 w-6" aria-hidden="true" />
                  </div>
                  <h3 className="mb-3 text-xl font-bold text-foreground">{title}</h3>
                  <p className="leading-relaxed text-muted-foreground">{description}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="px-4 py-20 sm:px-6 lg:px-8">
          <div className="mx-auto grid max-w-6xl items-center gap-12 lg:grid-cols-[1.05fr_0.95fr]">
            <div>
              <p className="mb-3 text-sm font-semibold uppercase tracking-[0.18em] text-cyan-400">
                {copy.supportLabel}
              </p>
              <h2 className="mb-5 text-3xl font-bold text-foreground sm:text-4xl">
                {copy.supportHeadline}
              </h2>
              <p className="text-lg leading-relaxed text-muted-foreground">
                {copy.supportIntro}
              </p>
            </div>

            <div className="rounded-2xl border border-border bg-card p-7 sm:p-8">
              <ul className="space-y-4">
                {copy.supportPoints.map((point) => (
                  <li key={point} className="flex items-start gap-3 text-foreground">
                    <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-cyan-400" aria-hidden="true" />
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        <section className="px-4 pb-20 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-5xl rounded-3xl border border-orange-500/30 bg-gradient-to-br from-orange-500/15 via-card to-blue-500/10 p-8 text-center sm:p-12">
            <h2 className="mb-4 text-3xl font-bold text-foreground sm:text-4xl">
              {copy.bottomHeadline}
            </h2>
            <p className="mx-auto mb-8 max-w-2xl text-lg text-muted-foreground">
              {copy.bottomIntro}
            </p>
            <Button
              asChild
              size="lg"
              className="h-auto min-h-11 max-w-full whitespace-normal py-3 text-center bg-gradient-to-r from-orange-500 to-red-600 font-semibold text-white hover:from-orange-600 hover:to-red-700"
            >
              <Link
                to={callHref("partner_page_bottom_cta", "openai-partner")}
                onClick={() => {
                  trackClick("OpenAI Partner Page - Bottom Schedule Call CTA", {
                    language: locale,
                    market,
                    location: "partner_page_bottom_cta",
                    destination: localizedPath("/ai-call", locale),
                  });
                }}
              >
                {copy.bottomCta}
                <ArrowRight aria-hidden="true" />
              </Link>
            </Button>
          </div>
        </section>

        <p className="mx-auto max-w-5xl px-4 pb-12 text-center text-xs text-muted-foreground sm:px-6 lg:px-8">

          {copy.disclaimer}

        </p>
      </main>

      <Footer locale={locale} />
    </div>
  );
};

export default OpenAIPartner;
